import { Badge, Box, Group, Stack, Tabs, Text } from "@mantine/core"
import { CheckIcon, XIcon } from "@phosphor-icons/react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import useSwr from "swr"
import { OrderApi } from "../../apis"
import { IOrderPlus } from "../../models/Order"
import { tugrug } from "../../utils"
import { dateTimeSecFormat } from "../../utils/date"
import { renderBusinessName } from "../../utils/render-business-name"
import { useSalesActions } from "../order/order-actions"
import { ListState } from "../ui"
import { PurchaseFilters } from "./list-filters"
import { RowActionIcons } from "./list-table"
import classes from "./supplier-purchase-kanban.module.css"
import { OrderStatusTag } from "./status-tag"

/**
 * Board columns in workflow order. The codes are the backend ORDER_STATUS codes (REJECTED, CANCELED);
 * any other status present in the data is appended, so no PO is ever left off the board.
 */
const STATUS_ORDER = ["REVIEWED", "APPROVED", "AUTHORIZED", "REJECTED", "CANCELED"]

type Props = {
  filters: PurchaseFilters
  onClick: (record: IOrderPlus) => void
  onAction: (key: "confirm" | "reject", record: IOrderPlus) => void
}

export function SupplierPurchaseKanban({ filters, onClick, onAction }: Props) {
  const { t } = useTranslation(["supplierSales", "common"])
  const { stateOf } = useSalesActions()

  const { data, isLoading, error, mutate } = useSwr(
    ["kanban-purchase", filters],
    async () => OrderApi.list({ filter: filters, offset: { page: 1, limit: 200 } }),
  )

  const grouped = useMemo(() => {
    const groups: Record<string, IOrderPlus[]> = {}
    ;(data?.rows || []).forEach((row: IOrderPlus) => {
      const status = row.orderStatus || "REVIEWED"
      ;(groups[status] = groups[status] || []).push(row)
    })
    return groups
  }, [data])

  const activeStatuses = useMemo(() => {
    const present = Object.keys(grouped)
    return [...STATUS_ORDER.filter((s) => present.includes(s)), ...present.filter((s) => !STATUS_ORDER.includes(s))]
  }, [grouped])

  const [tab, setTab] = useState<string | null>(null)
  useEffect(() => {
    if (activeStatuses.length && (!tab || !activeStatuses.includes(tab))) setTab(activeStatuses[0])
  }, [activeStatuses, tab])

  if (error) return <ListState state="error" onRetry={() => mutate()} />
  if (isLoading && !data) return <ListState state="loading" columns={4} />
  if (activeStatuses.length === 0) return <ListState state="empty" title={t("supplierSales:purchase.noData")} />

  const current = tab && activeStatuses.includes(tab) ? tab : activeStatuses[0]
  const items = grouped[current] || []

  const renderRow = (order: IOrderPlus) => {
    const buyer = renderBusinessName(order.senderBusiness?.partner, "") || renderBusinessName(order.senderBusiness) || "-"
    const approve = stateOf("approve", order)
    const decline = stateOf("decline", order)
    const actions = [
      ...(approve.applies ? [{ key: "approve", label: t("supplierSales:purchase.approve"), icon: <CheckIcon size={18} />, reason: approve.reason, onClick: () => onAction("confirm", order) }] : []),
      ...(decline.applies ? [{ key: "decline", label: t("supplierSales:purchase.reject"), icon: <XIcon size={18} />, reason: decline.reason, onClick: () => onAction("reject", order) }] : []),
    ]
    return (
      <Group key={order.id} className={classes.row} px="md" py="sm" wrap="nowrap" gap="md" onClick={() => onClick(order)}>
        {actions.length > 0 ? <Box w={88}><RowActionIcons items={actions} /></Box> : <Box w={88} />}
        <Box w={170}><OrderStatusTag code={order.orderStatus} perspective="received" /></Box>
        <Text fw={700} fz={14} ff="monospace" w={130} lineClamp={1}>{order.purchaseCode || "-"}</Text>
        <Text fz={14} flex={1} miw={0} lineClamp={1}>{buyer}</Text>
        <Text fw={700} fz={14} ff="monospace" ta="right" w={150}>{tugrug(order.orderAmount || 0)}</Text>
        <Text fz={14} c="gray.8" ta="right" w={150}>{order.createdAt ? dateTimeSecFormat(order.createdAt) : "-"}</Text>
      </Group>
    )
  }

  return (
    <Box>
      <Tabs value={current} onChange={setTab} keepMounted={false}>
        <Tabs.List>
          {activeStatuses.map((code) => (
            <Tabs.Tab key={code} value={code} rightSection={<Badge size="sm" radius="xl" variant="light" color="gray">{grouped[code]?.length || 0}</Badge>}>
              <OrderStatusTag code={code} perspective="received" />
            </Tabs.Tab>
          ))}
        </Tabs.List>
      </Tabs>

      <Group px="md" pt="md" pb="xs" wrap="nowrap" gap="md" className={classes.head}>
        <Box w={88}>{t("common:table.action")}</Box>
        <Box w={170}>{t("supplierSales:purchase.poStatus")}</Box>
        <Box w={130}>PO №</Box>
        <Box flex={1} miw={0}>{t("supplierSales:purchase.buyer")}</Box>
        <Box w={150} ta="right">{t("supplierSales:purchase.orderAmount")}</Box>
        <Box w={150} ta="right">{t("supplierSales:purchase.date")}</Box>
      </Group>

      <Stack gap="xs">{items.map(renderRow)}</Stack>
    </Box>
  )
}
